import React, { useState, useEffect, useContext } from 'react';
import { useOnboardingWizard } from '@/context/OnboardingWizardContext';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';
import { ValidationContext } from '@/components/validation';
import { LEDIndicator, type LEDState } from './LEDIndicator';

interface StepIndicatorProps {
  steps: string[];
  className?: string;
  variant?: 'sidebar' | 'strip';
  onStepClick?: () => void;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  className,
  variant = 'sidebar',
  onStepClick,
}) => {
  const validationCtx = useContext(ValidationContext);
  const { currentStep, goToStep, isStepCompleted } = useOnboardingWizard();
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isStepCompleted(currentStep) && !completedSteps.has(currentStep)) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
    }
  }, [currentStep, isStepCompleted, completedSteps]);

  const lastCompletedStep = steps.reduce(
    (last, _, idx) => (isStepCompleted(idx) ? idx : last),
    -1
  );
  const getLEDState = (index: number): LEDState => {
    if (index === currentStep) return 'active';
    if (isStepCompleted(index) || index < currentStep) return 'complete';
    return 'inactive';
  };

  const isReachable = (index: number): boolean => {
    // Check all prior steps for validation errors via the ValidationProvider
    if (validationCtx) {
      for (let i = 0; i < index; i++) {
        if (!validationCtx.isValid(String(i))) {
          return false;
        }
      }
    }
    return index <= lastCompletedStep + 1;
  };

  const handleClick = (index: number) => {
    if (!isReachable(index) || index === currentStep) {
      onStepClick?.();
      return;
    }
    toast.dismiss();
    goToStep(index);
    onStepClick?.();
  };

  if (variant === 'strip') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {steps.map((label, index) => (
          <button
            key={index}
            type="button"
            onClick={() => handleClick(index)}
            disabled={!isReachable(index)}
            className={cn(
              'flex items-center gap-1.5 transition-opacity',
              !isReachable(index) && 'opacity-40 cursor-not-allowed',
              index === currentStep && 'opacity-100',
              index !== currentStep && isReachable(index) && 'opacity-60 hover:opacity-80',
            )}
          >
            <LEDIndicator state={getLEDState(index)} />
            {index === currentStep && (
              <span className="font-heading text-xs uppercase tracking-wider text-foreground">
                {label}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  // Sidebar variant
  return (
    <nav className={cn('flex flex-col gap-1', className)}>
      {steps.map((label, index) => {
        const reachable = isReachable(index);
        const isActive = index === currentStep;

        return (
          <button
            key={index}
            type="button"
            onClick={() => handleClick(index)}
            disabled={!reachable}
            className={cn(
              'flex items-center gap-3 px-4 py-3 min-h-[44px] text-left transition-all duration-200 relative',
              'border-l-[3px] border-transparent',
              isActive && 'border-l-primary bg-surface-1',
              !reachable && 'opacity-40 cursor-not-allowed',
              reachable && !isActive && 'hover:bg-surface-1/50 cursor-pointer',
            )}
          >
            <LEDIndicator state={getLEDState(index)} />
            <span
              className={cn(
                'font-label text-[11px] uppercase tracking-[0.05em] transition-colors',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};
